import { useEffect, useState, FormEvent } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { AuthLayout } from '../components/AuthLayout';
import { supabase } from '../lib/supabase';
import { useSession } from '../lib/auth';
import { acceptInvitation } from '../lib/team';
import { validatePassword } from '../lib/passwordSecurity';

type Mode = 'choose' | 'signup' | 'signin' | 'accepting' | 'done' | 'error';

export function AcceptInvite() {
  const { token } = useParams<{ token: string }>();
  const { session, loading: sessionLoading } = useSession();
  const nav = useNavigate();

  const [mode, setMode] = useState<Mode>('choose');
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  // Sign-up / sign-in form state
  const [fullName, setFullName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [orgName, setOrgName] = useState<string | null>(null);

  // If the user is already signed in when they land here, just accept.
  useEffect(() => {
    if (!token || sessionLoading) return;
    if (!session) return;
    if (mode !== 'choose') return;
    void runAccept();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [session, sessionLoading, token]);

  async function runAccept() {
    if (!token) return;
    setMode('accepting');
    setError(null);
    try {
      const result = await acceptInvitation(token);
      setOrgName(result.org_name);
      setMode('done');
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
      setMode('error');
    }
  }

  async function onSignUp(e: FormEvent) {
    e.preventDefault();
    if (!token) return;
    setError(null);
    setSubmitting(true);
    try {
      // 12-char minimum + HIBP breach check. See lib/passwordSecurity.ts.
      const pwdError = await validatePassword(password);
      if (pwdError) {
        setError(pwdError);
        setSubmitting(false);
        return;
      }
      const { error: signUpError } = await supabase.auth.signUp({
        email: email.trim().toLowerCase(),
        password,
        options: {
          // Skip pending_org_name — they're joining an existing org, not creating one.
          data: { full_name: fullName },
          emailRedirectTo: `${window.location.origin}/invite/${token}`,
        },
      });
      if (signUpError) throw signUpError;

      // If Supabase has email confirmation enabled, there's no session yet —
      // the useEffect above will trigger acceptance after they click the email link.
      // If confirmation is disabled, they're already signed in — accept now.
      const { data: sess } = await supabase.auth.getSession();
      if (sess.session) {
        await runAccept();
      } else {
        setMode('signup'); // stay; show "check your email" below
      }
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setSubmitting(false);
    }
  }

  async function onSignIn(e: FormEvent) {
    e.preventDefault();
    if (!token) return;
    setError(null);
    setSubmitting(true);
    try {
      const { error: signInError } = await supabase.auth.signInWithPassword({
        email: email.trim().toLowerCase(),
        password,
      });
      if (signInError) throw signInError;
      await runAccept();
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setSubmitting(false);
    }
  }

  if (!token) {
    return (
      <AuthLayout title="Invitation link missing">
        <h1>Invitation link missing</h1>
        <p className="muted">This URL doesn't have an invite token. Ask whoever invited you to send the link again.</p>
        <p className="auth-footer"><Link to="/login">Back to sign in</Link></p>
      </AuthLayout>
    );
  }

  // Already signed in or just signed in → show acceptance status
  if (mode === 'accepting') {
    return (
      <AuthLayout title="Joining team">
        <h1>Joining…</h1>
        <p className="muted">One moment.</p>
      </AuthLayout>
    );
  }

  if (mode === 'done') {
    return (
      <AuthLayout title="Joined">
        <h1>You're in</h1>
        <p>
          Welcome to <strong>{orgName ?? 'the team'}</strong>. Tap below to start.
        </p>
        <button type="button" className="btn btn-primary" onClick={() => nav('/')}>
          Go to home →
        </button>
      </AuthLayout>
    );
  }

  if (mode === 'error') {
    return (
      <AuthLayout title="Invitation problem">
        <h1>This invitation can't be used</h1>
        <div className="banner banner-error" style={{ marginBottom: 12 }}>{error}</div>
        <p className="muted">
          The link may have expired (7-day limit), already been used, or been sent to a
          different email address than the one you've signed in with.
        </p>
        <p className="auth-footer">
          <Link to="/login">Back to sign in</Link>
        </p>
      </AuthLayout>
    );
  }

  // Not signed in — let them choose
  if (mode === 'choose') {
    return (
      <AuthLayout title="You've been invited">
        <h1>You've been invited</h1>
        <p className="muted" style={{ marginBottom: 16 }}>
          Someone's invited you to their team on Safe and Sorted. To join, set up an
          account or sign in if you've already got one.
        </p>
        <div className="btn-stack">
          <button type="button" className="btn btn-primary" onClick={() => setMode('signup')}>
            I'm new — set up my account
          </button>
          <button type="button" className="btn btn-ghost" onClick={() => setMode('signin')}>
            I already have an account
          </button>
        </div>
        <p className="muted" style={{ fontSize: 12, marginTop: 16, textAlign: 'center' }}>
          Use the email address the invite was sent to.
        </p>
      </AuthLayout>
    );
  }

  if (mode === 'signin') {
    return (
      <AuthLayout title="Sign in to accept">
        <h1>Sign in to accept</h1>
        <p className="muted">Use the same email the invite was sent to.</p>
        {error && <div className="banner banner-error">{error}</div>}
        <form onSubmit={onSignIn}>
          <div className="field">
            <label htmlFor="ai-email">Email</label>
            <input id="ai-email" type="email" required autoComplete="email" inputMode="email" enterKeyHint="next" autoFocus value={email} onChange={(e) => setEmail(e.target.value)} />
          </div>
          <div className="field">
            <label htmlFor="ai-password">Password</label>
            <input id="ai-password" type="password" required autoComplete="current-password" enterKeyHint="go" value={password} onChange={(e) => setPassword(e.target.value)} />
          </div>
          <button type="submit" className="btn btn-primary" disabled={submitting}>
            {submitting ? 'Signing in…' : 'Sign in & join team'}
          </button>
        </form>
        <p className="auth-footer">
          <button
          type="button"
          onClick={() => setMode('choose')}
          style={{ background: 'none', border: 'none', color: 'var(--primary)', textDecoration: 'underline', cursor: 'pointer', font: 'inherit', padding: 0 }}
        >← Back</button>
        </p>
      </AuthLayout>
    );
  }

  // mode === 'signup'
  return (
    <AuthLayout title="Set up your account">
      <h1>Set up your account</h1>
      <p className="muted">
        Two minutes. Your email needs to match the address the invite was sent to.
      </p>
      {error && <div className="banner banner-error">{error}</div>}
      <form onSubmit={onSignUp}>
        <div className="field">
          <label htmlFor="ai-name">Your name</label>
          <input id="ai-name" type="text" required autoComplete="name" enterKeyHint="next" autoFocus value={fullName} onChange={(e) => setFullName(e.target.value)} />
        </div>
        <div className="field">
          <label htmlFor="ai-su-email">Email</label>
          <input id="ai-su-email" type="email" required autoComplete="email" inputMode="email" enterKeyHint="next" value={email} onChange={(e) => setEmail(e.target.value)} />
        </div>
        <div className="field">
          <label htmlFor="ai-su-password">Password</label>
          <input id="ai-su-password" type="password" minLength={12} required autoComplete="new-password" enterKeyHint="go" value={password} onChange={(e) => setPassword(e.target.value)} />
          <p className="muted" style={{ marginTop: 6, fontSize: '0.85em' }}>
            At least 12 characters. A memorable passphrase like four random
            words is stronger than a short complex one.
          </p>
        </div>
        <button type="submit" className="btn btn-primary" disabled={submitting}>
          {submitting ? 'Creating account…' : 'Create account & join team'}
        </button>
      </form>
      <p className="auth-footer">
        <button
          type="button"
          onClick={() => setMode('choose')}
          style={{ background: 'none', border: 'none', color: 'var(--primary)', textDecoration: 'underline', cursor: 'pointer', font: 'inherit', padding: 0 }}
        >← Back</button>
      </p>
      <p className="muted" style={{ fontSize: 12, marginTop: 12 }}>
        If we ask you to verify your email, click the link we send and you'll come straight
        back here to finish joining.
      </p>
    </AuthLayout>
  );
}
